import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  CloudArrowUpIcon,
  CubeIcon,
  DocumentMagnifyingGlassIcon,
  FunnelIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import SEO from '../components/SEO';
import { useAppContext } from '../context/AppContext';
import { HomePageHeader } from './HomePageNew';
import { trackGoal } from '../utils/metrika';

const pageJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Подбор электротехники по спецификации, артикулам и ценам",
    "url": "https://get-goods.ai/podbor-elektrotehniki-po-specifikacii",
    "description": "Get-Goods.ai подбирает реальные электротехнические изделия, артикулы, бренды, аналоги и цены по загруженной спецификации."
  },
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Get-Goods.ai",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "url": "https://get-goods.ai/",
    "description": "ИИ-сервис для подбора электротехнической продукции по спецификациям, артикулам, брендам и ценам.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "RUB"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Что можно загрузить для подбора электротехники?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Можно загрузить спецификацию, перечень номенклатуры, таблицу из проектной документации или список позиций с техническими характеристиками."
        }
      },
      {
        "@type": "Question",
        "name": "Что Get-Goods.ai подбирает по спецификации?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Система подбирает реальные электротехнические изделия, артикулы производителей, бренды, аналоги, характеристики и цены, когда данные доступны в подключенных источниках."
        }
      },
      {
        "@type": "Question",
        "name": "Можно ли учитывать предпочтения по брендам?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Да. Результаты можно фильтровать и корректировать с учетом предпочтительных брендов, аналогов и требований закупки."
        }
      }
    ]
  }
];

const workflow = [
  {
    icon: CloudArrowUpIcon,
    title: "Загрузка спецификации",
    text: "Загрузите Excel, PDF или список позиций с описаниями, количеством и техническими требованиями."
  },
  {
    icon: DocumentMagnifyingGlassIcon,
    title: "Разбор позиций",
    text: "ИИ выделяет номенклатуру, параметры, единицы измерения, количество, комментарии и ограничения по брендам."
  },
  {
    icon: CubeIcon,
    title: "Поиск реальной продукции",
    text: "Система сопоставляет каждую позицию с товарами производителей, артикулами и доступными аналогами."
  },
  {
    icon: BanknotesIcon,
    title: "Цены и результат",
    text: "Вы получаете подборку с артикулами, брендами, характеристиками и ценами, если они доступны в источниках."
  }
];

const resultColumns = [
  "исходная позиция из спецификации",
  "подобранный бренд и артикул производителя",
  "ключевые технические характеристики",
  "варианты аналогов и замены",
  "цена, поставщик или источник данных",
  "комментарии и уверенность подбора"
];

const examples = [
  {
    source: "Авт. выкл. 3P C25 6кА",
    brand: "Schneider Electric",
    sku: "A9F79325",
    price: "цена из доступного источника"
  },
  {
    source: "Кабель ВВГнг(A)-LS 5х10",
    brand: "Кабельный завод",
    sku: "подбор по сечению и исполнению",
    price: "варианты по поставщикам"
  },
  {
    source: "Щит распределительный навесной IP54",
    brand: "IEK / EKF / аналоги",
    sku: "подбор по габаритам и степени защиты",
    price: "сравнение предложений"
  }
];

const SpecificationMatchingPage: React.FC = () => {
  const { isAuthenticated } = useAppContext();
  const targetPath = isAuthenticated ? "/my-applications" : "/login";
  const trackSeoClick = (source: string) => {
    trackGoal('seo_page_click', {
      source,
      seo_path: '/podbor-elektrotehniki-po-specifikacii',
      destination: targetPath,
      authenticated: isAuthenticated,
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Подбор электротехники по спецификации, артикулам и ценам - Get-Goods.ai"
        description="Get-Goods.ai подбирает реальные электротехнические изделия по спецификации: артикулы производителей, бренды, аналоги, характеристики и цены из доступных источников."
        canonicalPath="/podbor-elektrotehniki-po-specifikacii"
        imagePath="/logo512.png"
        keywords="подбор электротехники по спецификации, подбор артикулов, цены электротехники, аналоги электротехнического оборудования, подбор по брендам, спецификация оборудования"
        jsonLd={pageJsonLd}
      />
      <HomePageHeader />

      <main className="pt-20">
        <section className="bg-white">
          <div className="container mx-auto px-6 py-16 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-blue-600 mb-4">
                  Базовый инструмент Get-Goods.ai
                </p>
                <h1 className="text-4xl md:text-6xl font-extrabold leading-tight text-gray-900 mb-6">
                  Подбор электротехники по спецификации
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed mb-8">
                  Get-Goods.ai превращает спецификацию в подбор реальной продукции:
                  находит артикулы производителей, бренды, аналоги, характеристики
                  и цены электротехнических изделий.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to={targetPath}
                    onClick={() => trackSeoClick('specification_hero')}
                    className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-7 py-4 rounded-lg text-base font-semibold transition-colors duration-300 shadow-lg"
                  >
                    {isAuthenticated ? (
                      <CloudArrowUpIcon className="h-6 w-6 mr-2" />
                    ) : (
                      <SparklesIcon className="h-6 w-6 mr-2" />
                    )}
                    {isAuthenticated ? "Загрузить спецификацию" : "Начать подбор"}
                  </Link>
                  <a
                    href="#result"
                    className="inline-flex items-center justify-center border border-gray-300 hover:border-blue-500 text-gray-900 px-7 py-4 rounded-lg text-base font-semibold transition-colors duration-300"
                  >
                    Что на выходе
                  </a>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg bg-gray-50 shadow-xl overflow-hidden">
                <div className="bg-gray-900 text-white px-5 py-4 flex items-center justify-between">
                  <div className="font-semibold">Пример результата подбора</div>
                  <TagIcon className="h-6 w-6 text-blue-300" />
                </div>
                <div className="divide-y divide-gray-200">
                  {examples.map((item) => (
                    <div key={item.source} className="p-5 bg-white">
                      <div className="text-sm text-gray-500 mb-1">Исходная позиция</div>
                      <div className="font-semibold text-gray-900 mb-3">{item.source}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                        <div>
                          <div className="text-gray-500">Бренд</div>
                          <div className="font-medium text-gray-900">{item.brand}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Артикул</div>
                          <div className="font-medium text-gray-900">{item.sku}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Цена</div>
                          <div className="font-medium text-gray-900">{item.price}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
                Как работает подбор
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Сценарий рассчитан на снабжение, проектировщиков и подрядчиков,
                которым нужно быстро перейти от таблицы или перечня оборудования
                к понятным артикулам, брендам и ценам реальной продукции.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {workflow.map((step) => (
                <div key={step.title} className="border border-gray-200 rounded-lg p-6 bg-white">
                  <step.icon className="h-10 w-10 text-blue-600 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="result" className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <ClipboardDocumentListIcon className="h-10 w-10 text-blue-600" />
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                    Что получает пользователь
                  </h2>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Результат можно использовать для проверки спецификации, запроса цен,
                  сравнения брендов, подготовки закупки и замены позиций на аналоги.
                </p>
                <div className="space-y-4">
                  {resultColumns.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-5">
                <div className="bg-gray-900 text-white rounded-lg p-6">
                  <FunnelIcon className="h-10 w-10 text-blue-300 mb-4" />
                  <h3 className="text-2xl font-bold mb-3">Учет брендов и аналогов</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Можно сохранять предпочтения по брендам, смотреть альтернативы,
                    повторно подбирать позиции и вручную корректировать спорные результаты.
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                  <ArrowPathIcon className="h-10 w-10 text-blue-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Обновление подбора</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Если в спецификации меняются требования или бренд, подбор можно
                    пересчитать и сравнить новые варианты без ручного поиска по каталогам.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-blue-600 text-white">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-5">
              Загрузите спецификацию и получите подбор
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              Get-Goods.ai помогает быстрее найти реальные товары, артикулы,
              цены и аналоги вместо ручного поиска по каталогам и прайс-листам.
            </p>
            <Link
              to={targetPath}
              onClick={() => trackSeoClick('specification_bottom_cta')}
              className="inline-flex items-center justify-center bg-white text-blue-600 hover:text-blue-700 px-8 py-4 rounded-lg text-base font-semibold transition-colors duration-300 shadow-lg"
            >
              {isAuthenticated ? "Загрузить файл" : "Попробовать подбор"}
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SpecificationMatchingPage;
